// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the order and the colours of the real-time overlays (owner,
 * 2026-10-09: "move it up and down, change its palette and min/max,
 * nothing more, nothing less").
 *
 * Two pure pieces the browser panel, the phone's AR controls and the
 * headset all use, so the three cannot disagree:
 *
 *   - **The stack.** Overlays are kept bottom first (the first in the
 *     list is drawn lowest, right over the dataset). `moveInStack` takes
 *     one to another place in that list; nothing ever goes below the
 *     dataset, because the dataset is not in the list.
 *   - **The colours.** An overlay that carries values (a value-encoded
 *     release) is coloured by a table built from its encoding, the same
 *     way Tools → View colours the dataset (`buildDisplayLut`, and one row
 *     per class palette for a release with a class map). A picture, or a
 *     classified release whose classes have their own colours, has no
 *     palette to change.
 *
 * Every table is a new array: the globes cache their uploaded tables by
 * array identity, so a new array is what makes them draw the new colours.
 */

import { getLocale, t, type MessageKey } from '../i18n'
import { formatNumber } from '../i18n/format'
import {
  DEFAULT_DISPLAY,
  PALETTE_IDS,
  buildDisplayLut,
  displayGradientStops,
  isDefaultDisplay,
  valueAtPosition,
  type ColorScaleDisplay,
  type PaletteId,
} from './colorScaleDisplay'
import { buildReleaseLut, releaseColorScale, type ReleaseEncoding } from './dashRelease'
import { auxCropRect, phaseTables } from './releasePhase'
import { releaseOf, type ColorScale } from '../types/color-scale'

/** Where a release's class map sits in the frame, and how many palette rows it uses. */
export interface OverlayPhase {
  classMap: Uint8Array
  crop: { u0: number; v0: number; us: number; vs: number }
  rows: number
}

/** The colour table an overlay is drawn with, and its class map when it has one. */
export interface OverlayColours {
  lut: Uint8Array
  phase?: OverlayPhase
}

/** True when the overlay has values whose palette can change. */
export function canRecolour(enc: ReleaseEncoding | undefined | null): enc is ReleaseEncoding {
  return !!enc && enc.kind !== 'luma8-classified'
}

/** The scale the dialog shows limits in (units, the encoded range), or null for a picture. */
export function overlayScale(enc: ReleaseEncoding | undefined | null): ColorScale | null {
  return canRecolour(enc) ? releaseColorScale(enc) : null
}

/**
 * The tables to draw a release with under a display. The default display
 * gives exactly what the overlay had before this feature: the release's
 * own table.
 */
export function overlayColours(enc: ReleaseEncoding, display: ColorScaleDisplay = DEFAULT_DISPLAY): OverlayColours {
  const own = isDefaultDisplay(display) || !canRecolour(enc)
  const base = own ? buildReleaseLut(enc) : buildDisplayLut(releaseColorScale(enc), display)
  const tables = phaseTables(enc, base, own ? undefined : display)
  return {
    lut: tables?.palette ?? base,
    ...(tables ? { phase: { classMap: tables.classMap, crop: auxCropRect(enc, tables.aux), rows: tables.rows } } : {}),
  }
}

/** A display with this palette and these limits (physical values), keeping anything else it had. */
export function withPaletteAndLimits(
  scale: ColorScale,
  display: ColorScaleDisplay,
  palette: PaletteId,
  min: number,
  max: number,
): ColorScaleDisplay {
  // AYNI: as values on their own axis (a display group's, else the
  // stream's), so they may pass the encoded range, as Tools → View's do.
  const axis = display.limits?.scale ?? (releaseOf(scale)?.kind === 'luma8-log' ? 'log' : 'linear')
  if (axis === 'log' && !(min > 0)) return { ...display, palette }
  return { ...display, palette, stretch: { lo: 0, hi: 1 }, limits: { min, max, scale: axis } }
}

/** The limits a display draws between, as physical values. */
export function limitsOf(scale: ColorScale, display: ColorScaleDisplay): { min: number; max: number } {
  return { min: valueAtPosition(scale, display, 0), max: valueAtPosition(scale, display, 1) }
}

/** The ramp as colour stops, low first (for a swatch). */
export function rampStops(scale: ColorScale, display: ColorScaleDisplay, samples = 8): Array<[number, number, number]> {
  return displayGradientStops(scale, display, samples).map((s) => [s.rgba[0], s.rgba[1], s.rgba[2]])
}

/** The ramp as a CSS gradient, low end at `from`. */
export function rampGradient(scale: ColorScale, display: ColorScaleDisplay, direction = 'to top'): string {
  const stops = displayGradientStops(scale, display, 12)
    .map((s) => `rgb(${s.rgba[0]}, ${s.rgba[1]}, ${s.rgba[2]}) ${(s.position * 100).toFixed(0)}%`)
    .join(', ')
  return `linear-gradient(${direction}, ${stops})`
}

/** A limit for reading: three significant figures in the reader's language (0,5 in Spanish), and the units. */
export function formatLimit(value: number, units?: string | null): string {
  const text = Number.isFinite(value) ? formatNumber(Number(value.toPrecision(3)), { maximumSignificantDigits: 3, useGrouping: false }) : '—'
  return units ? `${text} ${units}` : text
}

/** How far one press of the headset's − / + moves a limit: a twentieth of the bar. */
export const LIMIT_STEP = 0.05
/** The narrowest the limits may get, as a fraction of the bar. */
const MIN_SPAN = 0.02

/**
 * One step of a limit (the headset has no keyboard for typing one): the
 * low or high end of the stretch moves a twentieth of the bar, which is
 * a twentieth of the colours on a log release too, and the two ends
 * never cross.
 */
export function stepLimit(display: ColorScaleDisplay, end: 'min' | 'max', dir: -1 | 1, step = LIMIT_STEP): ColorScaleDisplay {
  // AYNI: limits held as values (a display group's, or typed) move by a
  // twentieth of their own span, on their own axis, with no encoded range
  // to stop at; only the narrowest span holds them apart.
  const limits = display.limits
  if (limits && limits.min < limits.max && (limits.scale !== 'log' || limits.min > 0)) {
    const log = limits.scale === 'log'
    const [a, b] = log ? [Math.log10(limits.min), Math.log10(limits.max)] : [limits.min, limits.max]
    const span = b - a
    const nice = (v: number): number => Number(v.toPrecision(6))
    const back = (v: number): number => nice(log ? 10 ** v : v)
    if (end === 'min') {
      const next = Math.min(b - MIN_SPAN * span, a + dir * step * span)
      return { ...display, limits: { ...limits, min: back(next) } }
    }
    const next = Math.max(a + MIN_SPAN * span, b + dir * step * span)
    return { ...display, limits: { ...limits, max: back(next) } }
  }
  const lo = Math.min(display.stretch.lo, display.stretch.hi)
  const hi = Math.max(display.stretch.lo, display.stretch.hi)
  const round = (v: number): number => Math.round(v * 1000) / 1000
  if (end === 'min') {
    const next = round(Math.max(0, Math.min(hi - MIN_SPAN, lo + dir * step)))
    return next === lo ? display : { ...display, stretch: { lo: next, hi } }
  }
  const next = round(Math.min(1, Math.max(lo + MIN_SPAN, hi + dir * step)))
  return next === hi ? display : { ...display, stretch: { lo, hi: next } }
}

/**
 * A playing overlay as the headset's Layers page and the phone's AR
 * controls list it, with what its colour controls need.
 */
export interface RtPlayingEntry {
  id: string
  title: string
  /** Hidden on the globe with its eye (it keeps playing). */
  hidden: boolean
  /** AYNI (owner, 2026-10-12): its opacity, 1 opaque … 0 transparent (absent: 1). */
  opacity?: number
  /** Its colours, when they can change; null (or absent) for a picture or classes. */
  colours?: {
    palette: PaletteId
    /** AYNI (2026-10-20): the colours run the other way round (absent: no). */
    inverted?: boolean
    /** The ramp as it draws now, low first, CSS colours. */
    ramp: readonly string[]
    minLabel: string
    maxLabel: string
    /** Each palette as it would draw this overlay (the headset's choices). */
    palettes: ReadonlyArray<{ id: PaletteId; label: string; stops: readonly string[] }>
    /** The limits cannot go further that way. */
    atMin: { down: boolean; up: boolean }
    atMax: { down: boolean; up: boolean }
  } | null
  /** Why its colours cannot change, when they cannot. */
  coloursReason?: string
}

/** What the colour controls show for an overlay with values. */
export function entryColours(scale: ColorScale, display: ColorScaleDisplay): NonNullable<RtPlayingEntry['colours']> {
  const { min, max } = limitsOf(scale, display)
  return {
    palette: display.palette,
    inverted: !!display.inverted,
    ramp: rampStops(scale, display, 8).map(([r, g, b]) => `rgb(${r}, ${g}, ${b})`),
    minLabel: formatLimit(min, scale.units),
    maxLabel: formatLimit(max, scale.units),
    palettes: PALETTE_IDS.map((id) => ({
      id,
      label: t(`colorbar.palette.${id}` as MessageKey),
      stops: rampStops(scale, { ...DEFAULT_DISPLAY, palette: id, inverted: !!display.inverted }, 8).map(([r, g, b]) => `rgb(${r}, ${g}, ${b})`),
    })),
    atMin: { down: stepLimit(display, 'min', -1) === display, up: stepLimit(display, 'min', 1) === display },
    atMax: { down: stepLimit(display, 'max', -1) === display, up: stepLimit(display, 'max', 1) === display },
  }
}

/**
 * Move `id` to `to` in a bottom-first stack. `to` is clamped to the
 * stack; an unknown id leaves it as it was. Returns a new array.
 */
export function moveInStack<T extends string>(stack: readonly T[], id: T, to: number): T[] {
  const from = stack.indexOf(id)
  if (from < 0) return [...stack]
  const target = Math.max(0, Math.min(stack.length - 1, Math.round(to)))
  if (target === from) return [...stack]
  const next = stack.filter((x) => x !== id)
  next.splice(target, 0, id)
  return next
}

/**
 * Where a row dragged by `dy` pixels lands, in a list of rows `step`
 * pixels apart, starting from row `from` of `count`. Rows are top first.
 */
export function dropIndex(from: number, dy: number, step: number, count: number): number {
  if (count <= 1 || !(step > 0)) return Math.max(0, Math.min(count - 1, from))
  return Math.max(0, Math.min(count - 1, Math.round(from + dy / step)))
}

/** AYNI (owner, 2026-10-12): the step of the headset's − / + for a layer's transparency, in percent. */
export const TRANSPARENCY_STEP = 10

/** AYNI: an opacity one step more transparent (dir 1) or more opaque (dir -1), on the step's grid. */
export function stepOpacity(opacity: number, dir: -1 | 1): number {
  const percent = Math.round((1 - Math.min(1, Math.max(0, opacity))) * 100)
  const next = Math.min(100, Math.max(0, (dir > 0 ? Math.floor(percent / TRANSPARENCY_STEP) + 1 : Math.ceil(percent / TRANSPARENCY_STEP) - 1) * TRANSPARENCY_STEP))
  return 1 - next / 100
}

/** AYNI (owner, 2026-10-12): an opacity (1 opaque) as a transparency, 0 … 100 %. */
export function transparencyPercent(opacity: number): number {
  const clamped = Math.min(1, Math.max(0, Number.isFinite(opacity) ? opacity : 1))
  return Math.round((1 - clamped) * 100)
}

/** AYNI: a transparency as the reader's language writes a percentage (30 %, 30%). */
export function formatTransparency(percent: number): string {
  try {
    return new Intl.NumberFormat(getLocale(), { style: 'percent', maximumFractionDigits: 0 }).format(percent / 100)
  } catch {
    return `${percent}%`
  }
}
